import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useCustomerLeadsContext } from "./CustomerLeadsDataContext";

export function PostTrialEmailDialog({ show, onHide, lead, onConfirm, submitting }) {
  const { AUTOSELECTCOURSE } = useCustomerLeadsContext().TYPES;

  const [trialCourse, setTrialCourse] = useState(null);
  const [preferredCourse, setPreferredCourse] = useState(null);
  const [coachComment, setCoachComment] = useState("");
  const [couponText, setCouponText] = useState("");
  const [error, setError] = useState(null);

  // Pre-fill "course taken" from the lead's current trial course, and the
  // comment from the lead's existing coach_comment. "Preferred course" and
  // the coupon always start blank — they're picked fresh for every send.
  useEffect(() => {
    if (!show || !lead || !AUTOSELECTCOURSE) return;
    const currentTrialCourse =
      AUTOSELECTCOURSE.find((c) => c.id == lead.course_id) || null;
    setTrialCourse(currentTrialCourse);
    setPreferredCourse(null);
    setCoachComment(lead.coach_comment || "");
    setCouponText("");
    setError(null);
  }, [show, lead, AUTOSELECTCOURSE]);

  const handleHide = () => {
    if (submitting) return;
    onHide();
  };

  const handleConfirm = () => {
    if (!trialCourse || !preferredCourse) {
      setError("select-both");
      return;
    }
    setError(null);
    onConfirm({
      trial_course_id: trialCourse.id,
      preferred_course_id: preferredCourse.id,
      coach_comment: coachComment,
      coupon_text: couponText,
    });
  };

  const courseOptions = AUTOSELECTCOURSE
    ? AUTOSELECTCOURSE.slice().sort((a, b) =>
        b.primary_category.toString().localeCompare(a.primary_category.toString())
      )
    : [];

  return (
    <Modal show={show} onHide={handleHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Send Post-Trial Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Choose the course taken during the trial and a recommended program for
          registration. The recommended program is used to build a link to that
          program's page — it is not saved to this lead.
        </p>
        <div className="mb-4">
          <Autocomplete
            options={courseOptions}
            groupBy={(o) => o.primary_category}
            getOptionLabel={(o) => o.name}
            value={trialCourse}
            onChange={(event, newValue) => setTrialCourse(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Course Taken During Trial" variant="outlined" />
            )}
          />
        </div>
        <div>
          <Autocomplete
            options={courseOptions}
            groupBy={(o) => o.primary_category}
            getOptionLabel={(o) => o.name}
            value={preferredCourse}
            onChange={(event, newValue) => setPreferredCourse(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Preferred Course for Registration" variant="outlined" />
            )}
          />
        </div>
        {error === "select-both" && (
          <div className="text-danger small mt-3">Please select both courses.</div>
        )}
        <div className="mt-4">
          <TextField
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Coach's Comment/Feedback"
            value={coachComment}
            onChange={(e) => setCoachComment(e.target.value)}
            inputProps={{ maxLength: 225 }}
            helperText={
              'This will be included in the email the parent receives, as "Counselor\'s Note" — review and edit it so it reads naturally to them.'
            }
          />
        </div>
        <div className="mt-4">
          <TextField
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            label="Coupon (optional)"
            value={couponText}
            onChange={(e) => setCouponText(e.target.value)}
            helperText={
              'Optional. Appears in the email as "A Special offer for you: …". Example: "SAVE10 – $10 off your next session, valid through 12/31."'
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleConfirm} disabled={submitting}>
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              {" "}Sending…
            </>
          ) : (
            "Confirm and send email"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
